import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { API_URL } from '../config/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  const fetchProfile = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else if (response.status === 403) {
        // Token might be invalid/expired - don't clear profile, let user retry
        console.error('Profile fetch returned 403 - token may be expired');
      } else {
        console.error('Profile fetch failed:', response.status);
      }
    } catch (error) {
      // Network error - don't clear profile, keep existing state
      console.error('Failed to fetch profile (network):', error);
    }
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setUser(session.user);
          setAccessToken(session.access_token);
          // Fetch profile in background - don't block initial load
          fetchProfile(session.access_token);
        }
      } catch (err) {
        console.error('Session check failed:', err);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session ? 'session exists' : 'no session');
        
        if (event === 'TOKEN_REFRESHED' && session) {
          // Token was refreshed - update the access token
          setAccessToken(session.access_token);
          return;
        }
        
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setAccessToken(null);
          setProfile(null);
          return;
        }
        
        if (session) {
          setUser(session.user);
          setAccessToken(session.access_token);
          // Fetch profile in background - don't block auth state changes
          fetchProfile(session.access_token);
        }
        // Don't clear state for other events - could be temporary token refresh issues
      }
    );

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signup = async (email, password) => {
    setLoading(true);
    try {
      // Use Supabase client directly for signup
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        // Handle rate limiting
        if (error.status === 429) {
          throw new Error('Too many signup attempts. Please wait a minute and try again.');
        }
        throw new Error(error.message || 'Signup failed');
      }
      
      // Check if email confirmation is required
      if (data?.user && !data?.session) {
        // Email confirmation required - this is normal for Supabase
        return { 
          user: data.user, 
          session: null, 
          needsConfirmation: true 
        };
      }
      
      // If we got a session, user is logged in (email confirmation disabled in Supabase)
      if (data?.session) {
        setUser(data.user);
        setAccessToken(data.session.access_token);
        await fetchProfile(data.session.access_token);
        return { user: data.user, session: data.session, needsConfirmation: false };
      }
      
      // Default case - user created but needs confirmation
      return { user: data?.user, session: null, needsConfirmation: true };
    } catch (err) {
      // Handle Supabase specific errors
      const errorMessage = err?.message || 'Signup failed';
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const signin = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        if (error.status === 429) {
          throw new Error('Too many login attempts. Please wait a minute and try again.');
        }
        throw new Error(error.message || 'Invalid email or password');
      }
      
      setUser(data.user);
      setAccessToken(data.session.access_token);
      // Fetch profile in background - don't block login completion
      fetchProfile(data.session.access_token);
      
      return data;
    } catch (err) {
      const errorMessage = err?.message || 'Invalid email or password';
      throw new Error(errorMessage);
    }
  };

  const signout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setAccessToken(null);
      setProfile(null);
    } catch (err) {
      console.error('Signout error:', err);
      // Clear state anyway
      setUser(null);
      setAccessToken(null);
      setProfile(null);
    }
  };

  // Try to refresh the session token
  const refreshSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Session refresh failed:', error);
        return null;
      }
      if (session) {
        setAccessToken(session.access_token);
        return session.access_token;
      }
    } catch (err) {
      console.error('Session refresh error:', err);
    }
    return null;
  };

  const refreshProfile = async () => {
    if (accessToken) {
      await fetchProfile(accessToken);
    }
  };

  // Calculate generation limits from profile
  const isSubscribed = profile?.is_subscribed || false;
  const remainingGenerations = isSubscribed ? -1 : (profile?.remaining_generations ?? 5);
  const dailyLimit = profile?.daily_limit ?? 5;
  const canGenerate = isSubscribed || remainingGenerations > 0;

  const value = {
    user,
    accessToken,
    loading,
    profile,
    signup,
    signin,
    signout,
    refreshProfile,
    refreshSession,
    isAuthenticated: !!user,
    isSubscribed,
    canGenerate,
    remainingGenerations,
    dailyLimit
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
